/**
 * Shared types and utilities for void renderer subsystems.
 */

import type { Camera } from 'bonkjs';
import type { ParticleSystem } from '../particles';
import type { ScreenEffects } from '../effects';
import type { ScreenShake } from '../screen-shake';
import { WORLD } from '../../constants';
import type { StageTheme } from '../../stages';
import { getActiveTheme, DEFAULT_STAGE_ID } from '../../stages';

// ── Interfaces ──────────────────────────────────────────────────────────

export interface GlitchBar {
  x: number;
  width: number;
  yOffset: number;  // base offset from voidY
  phase: number;    // sine phase
  color: number;    // magenta or purple
}

export interface AmbientMote {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  radius: number;
  color: number;
}

export interface Bubble {
  x: number;
  y: number;
  vy: number;          // upward speed (negative)
  radius: number;
  phase: number;       // wobble sine phase
  color: number;
  life: number;
  maxLife: number;
  popping: boolean;    // expanding+fading at edge
  popTimer: number;
}

export interface SlashMark {
  x: number;
  baseY: number;        // edge Y at spawn
  arcAngle: number;     // rotation (upward-biased)
  arcRadius: number;    // 20-50px
  arcSweep: number;     // PI/3 to PI/2 radians
  life: number;
  maxLife: number;
  color: number;
  width: number;
  alpha: number;        // peak alpha
}

export interface PlayerPos {
  x: number;
  y: number;
  alive: boolean;
}

/** Shared mutable state passed to all void subsystem functions. */
export interface VoidState {
  // Edge distortion (kill bump)
  edgeDistortionX: number;
  edgeDistortionStrength: number;

  // Liquid edge
  edgeWaveAmp: number;       // multiplier, 1=calm, 6=rise
  edgeTime: number;          // accumulated time for wave animation

  // Edge Y cache — populated by drawLiquidEdge(), consumed by updateBubbles()
  cachedEdgeYs: number[];
  cachedEdgeSegWidth: number;

  // Rise choreography
  riseIntensity: number;      // 0-1, drives glitch/bubble/wave amplification
  risePhaseTimer: number;     // seconds since triggerRiseEffects
  risePhaseActive: boolean;

  // Mood-driven visual intensity (0-1, mapped from game state voidMood 0-100)
  moodIntensity: number;
  phase2Fired: boolean;       // flash gate
  phase3Fired: boolean;       // edge particles gate

  // Sweep trail
  previousVoidY: number;
  sweepTrailAlpha: number;
  sweepTrailStartY: number;

  // Gradient throttle
  lastDrawnVoidY: number;
  gradientFrameSkip: number;

  // Glitch
  glitchBars: GlitchBar[];
  glitchTimer: number;

  // Ambient particles
  ambientMotes: AmbientMote[];

  // Bubbles
  bubbles: Bubble[];

  // Slash marks
  slashMarks: SlashMark[];

  // Proximity
  playerPositions: PlayerPos[];

  // Stored refs for delayed rise effects
  particlesRef: ParticleSystem | null;
  effectsRef: ScreenEffects | null;
  cameraRef: Camera | null;
  screenShakeRef: ScreenShake | null;
}

// ── Void Theme ──────────────────────────────────────────────────────────

/** Module-level void theme colors — updated via setVoidTheme(). */
let _voidTheme: StageTheme = getActiveTheme(DEFAULT_STAGE_ID);

export function setVoidTheme(stageId: string): void {
  _voidTheme = getActiveTheme(stageId);
}

export function getVoidTheme(): StageTheme {
  return _voidTheme;
}

// ── Utilities ───────────────────────────────────────────────────────────

/** World-space extent for void rendering — covers full world width (or viewport if wider). */
export function visibleX(): { off: number; w: number } {
  const viewW = WORLD.CANVAS_WIDTH / WORLD.SCALE;
  const w = Math.max(viewW, WORLD.VIRTUAL_WIDTH);
  return { off: (WORLD.VIRTUAL_WIDTH - w) / 2, w };
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
